import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, message, Modal } from "antd";
import {
  LockOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { LoginForm } from "../types";
import { AuthService } from "../services";
import TokenManager from "../utils/tokenManager";
import ErrorHandler from "../utils/errorHandler";
import "../styles/Login.scss";

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const navigate = useNavigate();

  // 图片验证码相关状态
  const [captchaId, setCaptchaId] = useState<string>("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaModalVisible, setCaptchaModalVisible] = useState(false);
  const [captchaCode, setCaptchaCode] = useState<string>("");
  const [svgData, setSvgData] = useState<string>("");

  // 加载图片验证码
  const loadCaptcha = async (phone?: string) => {
    try {
      setCaptchaLoading(true);
      const phoneNumber = phone || form.getFieldValue("phoneNumber");

      if (!phoneNumber || !/^1[3-9]\d{9}$/.test(phoneNumber)) {
        message.error("请先输入正确的手机号码");
        return false;
      }

      // 调用验证码 API
      const response = await AuthService.getCaptcha(phoneNumber, "image");

      if (response.code === 0 && response.data) {
        // 从响应中获取验证码信息
        const { captchaId: id, svgData } = response.data;

        if (id && svgData) {
          setCaptchaId(id);
          setSvgData(svgData);
          return true;
        } else {
          message.error("获取验证码失败");
          return false;
        }
      } else {
        message.error(response.msg || "获取验证码失败");
        return false;
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      return false;
    } finally {
      setCaptchaLoading(false);
    }
  };

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 打开验证码弹窗
  const showCaptchaModal = async () => {
    try {
      // 验证手机号
      await form.validateFields(["phoneNumber"]);
      const success = await loadCaptcha();
      if (success) {
        setCaptchaModalVisible(true);
        setCaptchaCode(""); // 清空之前输入的验证码
      }
    } catch (error) {
      // 表单验证失败
      console.error("表单验证失败:", error);
    }
  };

  // 刷新验证码
  const refreshCaptcha = () => {
    setCaptchaCode(""); // 清空输入的验证码
    loadCaptcha();
  };

  // 发送验证码
  const handleSendCode = async () => {
    if (!captchaId || !captchaCode) {
      message.error("请先完成图片验证码验证");
      return;
    }

    try {
      const phoneNumber = form.getFieldValue("phoneNumber");
      setSendingCode(true);

      // 调用发送验证码 API
      const response = await AuthService.sendVerificationCode({
        phoneNumber,
        captchaId,
        captchaCode,
      });

      if (response.code === 0) {
        message.success("验证码已发送到您的手机");
        setCountdown(60);
        setCaptchaModalVisible(false); // 关闭验证码弹窗
        form.setFieldsValue({ captchaCode }); // 保存验证码到表单
      } else {
        message.error(response.msg || "发送验证码失败");
        // 刷新验证码
        refreshCaptcha();
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      // 刷新验证码
      refreshCaptcha();
    } finally {
      setSendingCode(false);
    }
  };

  // 处理登录
  const handleLogin = async (formData: LoginForm) => {
    setLoading(true);

    try {
      // 调用登录 API
      const response = await AuthService.login({
        ...formData,
        captchaId,
        captchaCode: formData.captchaCode || captchaCode, // 使用表单中的验证码或状态中的验证码
      });

      if (response.code === 0 && response.data) {
        // 保存 token 到本地存储
        TokenManager.setToken(response.data.accessToken);

        message.success("登录成功！");

        // 从 token 中获取用户角色，根据角色跳转
        const userInfo = TokenManager.getUserFromToken(
          response.data.accessToken
        );
        const uuserRoles = userInfo?.roles;

        // admin 用户跳转到用户管理页面，其他用户跳转到 Token 记录页面
        if (uuserRoles.includes("admin")) {
          navigate("/users");
        } else {
          navigate("/token-records");
        }
      } else {
        message.error(response.msg || "登录失败");
        // 刷新验证码
        refreshCaptcha();
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      // 刷新验证码
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <Card className="login-card">
          <div className="login-header">
            <h1>用户管理后台</h1>
            <p>暂时只支持手机验证登录</p>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleLogin}
            autoComplete="off"
            className="login-form"
          >
            <Form.Item
              name="phoneNumber"
              label="手机号码"
              rules={[
                { required: true, message: "请输入手机号码" },
                { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号码" },
              ]}
            >
              <Input
                prefix={<MobileOutlined />}
                placeholder="请输入手机号码"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="code"
              label="短信验证码"
              rules={[
                { required: true, message: "请输入验证码" },
                { len: 6, message: "验证码为6位数字" },
              ]}
            >
              <div className="code-input-group">
                <Input
                  prefix={<LockOutlined />}
                  placeholder="请输入6位验证码"
                  size="large"
                  maxLength={6}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={showCaptchaModal}
                  loading={sendingCode}
                  disabled={countdown > 0}
                  className="send-code-btn"
                >
                  {countdown > 0 ? `${countdown}s` : "发送验证码"}
                </Button>
              </div>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                block
                className="login-btn"
              >
                登录
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>

      {/* 图片验证码弹窗 */}
      <Modal
        title="请完成图片验证"
        open={captchaModalVisible}
        width={350}
        onCancel={() => setCaptchaModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setCaptchaModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={sendingCode}
            onClick={handleSendCode}
            disabled={!captchaCode}
          >
            确认并发送验证码
          </Button>,
        ]}
      >
        <div className="captcha-modal-content">
          <div className="captcha-image-container" onClick={refreshCaptcha}>
            {captchaLoading ? (
              <div className="captcha-loading">加载中...</div>
            ) : svgData ? (
              <>
                <div dangerouslySetInnerHTML={{ __html: svgData }} />
                <div className="captcha-tip">点击图片刷新验证码</div>
              </>
            ) : (
              <div className="captcha-error">验证码加载失败，请点击重试</div>
            )}
          </div>
          <Input
            prefix={<SafetyCertificateOutlined />}
            placeholder="请输入图片验证码"
            value={captchaCode}
            onChange={(e) => setCaptchaCode(e.target.value)}
            maxLength={6}
            size="large"
            className="captcha-input"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Login;
